import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { getModuleIcon } from "@/utils/module-icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import type { Module } from "@shared/schema";

const MODULE_I18N_KEY: Record<string, string> = {
  "offer-builder": "modules.offerBuilder",
  "vsl-creator": "modules.vslCreator",
  "ad-creator": "modules.adCreator",
  "marketing-analyst": "modules.marketingAnalyst",
  "campaign-creator": "modules.campaignCreator",
  "content-creator": "modules.contentCreator",
  "allegro-ads": "modules.allegroAds",
  "reel-creator": "modules.reelCreator",
  "idea-bank": "modules.ideaBank",
  "youtube-mode": "modules.youtubeMode",
};

const SYSTEM_HIDDEN = new Set([
  "clone-yourself",
  "email-creator",
  "reel-creator",
  "youtube-mode",
  "idea-bank",
  "carousel-creator",
]);

interface GridModuleItemProps {
  module: Module;
  isActive: boolean;
  onClick: () => void;
}

function GridModuleItem({ module, isActive, onClick }: GridModuleItemProps) {
  const { t } = useTranslation();
  const i18nKey = MODULE_I18N_KEY[module.id];
  const label = i18nKey ? t(i18nKey) : module.name;
  const isLocked = module.locked || false;

  return (
    <motion.button
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ type: "spring", stiffness: 400, damping: 35, mass: 0.5 }}
      onClick={() => onClick()}
      className={`flex items-center gap-3 px-3 py-2 rounded-xl w-full text-left group transition-colors duration-100 ${
        isLocked
          ? "opacity-60 grayscale-[0.5]"
          : isActive
            ? "bg-[#ef3e29]/10"
            : "hover:bg-white/[0.05]"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-100 relative ${
          isActive ? "bg-[#ef3e29]/20" : "bg-white/[0.06] group-hover:bg-white/[0.1]"
        }`}
      >
        {getModuleIcon(module.id, "w-4 h-4", isActive ? "colored" : "default")}
        {isLocked && <Lock className="w-3 h-3 text-white/40 absolute" />}
      </div>
      <span
        className={`text-sm font-medium transition-colors duration-100 truncate ${
          isActive ? "text-white" : "text-white/60 group-hover:text-white/90"
        }`}
      >
        {label}
      </span>
    </motion.button>
  );
}

export interface SidebarModuleListProps {
  modules: Module[];
  currentModule: string;
  onModuleSelect: (moduleId: string, locked: boolean) => void;
  hiddenModuleIds?: Set<string>;
}

export function SidebarModuleList({
  modules,
  currentModule,
  onModuleSelect,
  hiddenModuleIds = new Set(),
}: SidebarModuleListProps) {
  const { user } = useAuth();
  const isEcommercePlan = (user?.subscriptionPlanKey ?? "").startsWith("ecommerce");

  const visibleModules = useMemo(
    () =>
      modules.filter((m) => {
        if (SYSTEM_HIDDEN.has(m.id) || hiddenModuleIds.has(m.id)) return false;
        if (m.id === "allegro-ads" && !isEcommercePlan) return false;
        return true;
      }),
    [modules, hiddenModuleIds, isEcommercePlan]
  );

  return (
    <div className="py-2">
      <div className="flex flex-col">
        {visibleModules.map((module) => (
          <GridModuleItem
            key={module.id}
            module={module}
            isActive={currentModule === module.id}
            onClick={() => onModuleSelect(module.id, module.locked || false)}
          />
        ))}
      </div>
    </div>
  );
}
